import { useEffect } from 'react'
import { Loader } from 'src/applications/Embed/fragments/Loader'
import { IBatch } from 'src/applications/Embed/types/IBatch'
import { SCENE } from 'src/applications/Embed/types/IScene'
import { Colors } from 'src/resources/colors'
import { FlatButton } from 'src/resources/elements/buttons/FlatButton'
import { Lightbulb } from 'src/resources/elements/Icons'
import { Spacing } from 'src/resources/layout'
import { useToasts } from 'react-toast-notifications'
import { fontSizes } from 'src/resources/typography'
import { getRoundedNumber } from 'src/resources/utils/getRoundedNumber'
import { useSmartQuery } from 'src/smart/hooks/useSmartQuery'
import { SQ_GET_UPLOAD } from 'src/smart/queries/SQ_GET_UPLOAD'
import { uploadUpdatedSubscriptionWithFallback } from 'src/smart/queries/SQ_UPLOAD_UPDATES_SUBSCRIPTION'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'

interface IEmbedNextAction {
  batch: IBatch
  onReset(): void
  onNextAction(scene: SCENE, b?: Partial<IBatch>): void
}

const SuggestionBox = styled.div`
  display: flex;
  padding: 0 ${Spacing.basePadding2x};
  column-gap: ${Spacing.basePadding2x};
  align-items: center;
  margin-top: ${Spacing.basePadding4x};
  p {
    font-size: ${fontSizes.type15};
  }
  max-width: 400px;
  background-color: ${Colors.pigeon100};
  border-radius: 6px;
  svg {
    height: ${Spacing.basePadding4x};
    width: ${Spacing.basePadding4x};
  }
`

export function EmbedNextAction({ batch, onReset, onNextAction }: IEmbedNextAction) {
  const { addToast } = useToasts()
  const uploadQuery = useSmartQuery(SQ_GET_UPLOAD, {
    fetchPolicy: 'network-only',
    skip: !batch?.uploadId,
    variables: { uploadId: batch.uploadId }
  })

  const { result, fallbackResult } = uploadUpdatedSubscriptionWithFallback(
    batch.workspaceId,
    batch.uploadId
  )

  const { t } = useTranslation()

  useEffect(() => {
    if (result?.updateType && result?.updateType === 'uploadInProgress') {
      uploadQuery.state.startPolling(1000)
    }
  }, [result])

  const nextAction = uploadQuery?.result?.nextAction?.name || fallbackResult?.nextAction?.name

  useEffect(() => {
    const uploadDataSources =
      uploadQuery.result?.uploadDataSources || fallbackResult?.uploadDataSources
    const hasDataSources = uploadDataSources?.length > 0
    const viewId =
      uploadQuery.result?.nextAction?.originalViewId || fallbackResult?.nextAction?.originalViewId
    const dataSourceIndex =
      (uploadQuery.result?.selectedDataSourceIndex || fallbackResult?.selectedDataSourceIndex) ?? 0

    if (nextAction === 'evaluate') {
      onNextAction(SCENE.FINAL)
    }

    if (nextAction === 'review') {
      uploadQuery.state.stopPolling()
      onNextAction(SCENE.REVIEW)
    } else if (nextAction === 'header') {
      onNextAction(SCENE.HEADER_CHECK, {
        dataSourceIndex
      })
    } else if (nextAction === 'match') {
      uploadQuery.state.stopPolling()
      onNextAction(SCENE.MATCH, {
        viewId
      })
    } else if (nextAction === 'source' && hasDataSources) {
      uploadQuery.state.stopPolling()
      onNextAction(SCENE.SOURCE, {
        viewId,
        uploadDataSources
      })
    } else if (nextAction === 'error') {
      addToast('Invalid file format. Please select a different file.', {
        appearance: 'error',
        autoDismiss: true
      })

      onReset()
    }
  }, [uploadQuery?.result?.uploadDataSources, uploadQuery?.result?.nextAction?.name])

  const percentCompleteRounded = getRoundedNumber(uploadQuery?.result?.percentComplete)

  return (
    <Loader
      message={t('components.EmbedNextAction.processingFile', {
        fileName: uploadQuery?.result?.fileName
      })}
      top={0}
      padding={40}
      fontSize={21}
      percentage={percentCompleteRounded}
      displayLoadingCircle={percentCompleteRounded > 0}
    >
      {uploadQuery?.result?.nextAction?.suggestCSVConversion && (
        <SuggestionBox>
          <Lightbulb />
          <p>{t('components.EmbedNextAction.csvConvert')}</p>
        </SuggestionBox>
      )}
      <FlatButton
        margin={{ top: Spacing.basePadding4x }}
        variant='outlined'
        onClick={onReset}
        color={'primary'}
      >
        {t('elements.buttons.cancel')}
      </FlatButton>
    </Loader>
  )
}
